import MapboxDraw from '@mapbox/mapbox-gl-draw';
import numeral from 'numeral';
import _ from 'lodash';
import * as turf from '@turf/turf';

const RectMode = _.extend({}, MapboxDraw.modes.draw_line_string);

function createVertex(parentId, coordinates, path, selected) {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [...coordinates],
    },
    properties: {
      name: 'vertex',
      meta: 'vertex',
      parent: parentId,
      coord_path: path,
      active: selected ? 'true' : 'false',
    },
  };
}

function createGeoJSONRect(startPoint, endPoint, parentId) {
  const ret = [startPoint];

  if(startPoint && endPoint) {
    const distance = turf.distance(startPoint, endPoint)
    const bearing = turf.bearing(startPoint, endPoint)

    const point2 = turf.destination(startPoint, distance, bearing - 0).geometry.coordinates
    const point3 = turf.destination(point2, distance, turf.bearing(startPoint, point2) - 90).geometry.coordinates
    const point4 = turf.destination(startPoint, distance, bearing - 90).geometry.coordinates

    if (endPoint) {
      ret.push(point2);
      ret.push(point3);
      ret.push(point4);
    }
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [ret],
    },
    properties: {
      name: '',
      meta: '',
      parent: parentId,
    },
  };
}

const doubleClickZoom = {
  enable: (ctx) => {
    setTimeout(() => {
      if (
        !ctx.map ||
        !ctx.map.doubleClickZoom ||
        !ctx._ctx ||
        !ctx._ctx.store ||
        !ctx._ctx.store.getInitialConfigValue
      )
        return;
      if (!ctx._ctx.store.getInitialConfigValue('doubleClickZoom')) return;
      ctx.map.doubleClickZoom.enable();
    }, 0);
  },
};

RectMode.onSetup = function (opts) {
  const props = MapboxDraw.modes.draw_line_string.onSetup.call(this, opts);
  const rect = this.newFeature({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [],
    },
    properties: {
    name: 'rectangle',
      meta: 'rectangle',
    },
  });
  this.addFeature(rect);

  const keyUpFn = ((event) => {
    if (event.key === 'Escape') {
      this.map.fire('draw.keyup.escape', {
        feature: rect
      })
    }
    document.removeEventListener('keyup', keyUpFn)
  })
  document.addEventListener('keyup', keyUpFn)

  return {
    ...props,
    rect,
  };
};

function getDisplayMeasurements(feature) {
  const drawnLength = turf.lineDistance(feature) * 1000; // meters

  let metricUnits = 'm';
  let metricFormat = '0,0';
  let metricMeasurement;

  let standardUnits = 'feet';
  let standardFormat = '0,0';
  let standardMeasurement;

  metricMeasurement = drawnLength;
  if (drawnLength >= 1000) {
    metricMeasurement = drawnLength / 1000;
    metricUnits = 'km';
    metricFormat = '0.00';
  }

  standardMeasurement = drawnLength * 3.28084;
  if (standardMeasurement >= 5280) {
    standardMeasurement /= 5280;
    standardUnits = 'mi';
    standardFormat = '0.00';
  }

  const displayMeasurements = {
    metric: `${numeral(metricMeasurement).format(metricFormat)} ${metricUnits}`,
    standard: `${numeral(standardMeasurement).format(
      standardFormat
    )} ${standardUnits}`,
  };

  return displayMeasurements;
}

RectMode.clickAnywhere = function (state, e) {
  if (state.currentVertexPosition === 1) {
    state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
    return this.changeMode('simple_select', { featureIds: [state.line.id] });
  }
  this.updateUIClasses({ mouse: 'add' });
  state.line.updateCoordinate(
    state.currentVertexPosition,
    e.lngLat.lng,
    e.lngLat.lat
  );
  if (state.direction === 'forward') {
    state.currentVertexPosition += 1;
    state.line.updateCoordinate(
      state.currentVertexPosition,
      e.lngLat.lng,
      e.lngLat.lat
    );
  } else {
    state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
  }

  return null;
};

RectMode.onMouseMove = function (state, e) {
  MapboxDraw.modes.draw_line_string.onMouseMove.call(this, state, e);
  const geojson = state.line.toGeoJSON();
  const start = geojson.geometry.coordinates[0];
  const radiusInKm = turf.lineDistance(geojson, { units: 'kilometers' });
  const end = geojson.geometry.coordinates[1];
  const feature = createGeoJSONRect(start, end, state.line.id);
  feature.properties.meta = 'rect';
  state.rect.setCoordinates(feature.geometry.coordinates);
};

RectMode.onStop = function (state) {
  doubleClickZoom.enable(this);

  this.activateUIButton();

  if (this.getFeature(state.line.id) === undefined) return;

  state.line.removeCoordinate('0');

  if (state.line.isValid() || state.rect.isValid()) {
    const geojson = state.rect.toGeoJSON();
    this.deleteFeature([state.line.id], { silent: true });

    this.map.fire('draw.create', {
      features: [geojson],
    });
  } else {
    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode('simple_select', {}, { silent: true });
  }
};

RectMode.toDisplayFeatures = function (state, geojson, display) {
  const isActiveLine = geojson.properties.id === state.line.id;
  geojson.properties.active = isActiveLine ? 'true' : 'false';
  if (!isActiveLine) return display(geojson);

  if (geojson.geometry.coordinates.length < 2) return null;
  geojson.properties.meta = 'feature';

  display(
    createVertex(
      state.line.id,
      geojson.geometry.coordinates[
        state.direction === 'forward'
          ? geojson.geometry.coordinates.length - 2
          : 1
      ],
      `${
        state.direction === 'forward'
          ? geojson.geometry.coordinates.length - 2
          : 1
      }`,
      false
    )
  );

  display(geojson);

  const displayMeasurements = getDisplayMeasurements(geojson);

  const currentVertex = {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [...geojson.geometry.coordinates[1]],
    },
    properties: {
      name: 'currentPosition',
      meta: 'currentPosition',
      radiusMetric: displayMeasurements.metric,
      radiusStandard: displayMeasurements.standard,
      parent: state.line.id,
    },
  };
  display(currentVertex);

  return null;
};

export default RectMode;
